import { ChangeEvent, FC, useContext, useLayoutEffect, useRef } from "react";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import { QESRadioGroup } from "@app/shared/qes-standards/forms/RadioGroup";
import { SkipTimeDistanceRecalcCallout } from "@app/shared/terminations/SkipTimeDistanceRecalcCallout";

interface DistanceMeasurementRadioGroupProps {
  /**
   * The "name" attribute shared by the two radio inputs. Must be unique on the page if this
   * component is rendered more than once.
   */
  name: string;
  title: string;
  isZipProject: boolean;
  hasUnprocessedProviderTerminations?: boolean;
  shouldSkip: boolean;
  /**
   * Called with the current selection — both on user interaction and once on mount to report the default
   */
  setShouldSkip: (shouldSkip: boolean) => void;
  computeTestId: string;
  skipTestId: string;
  /**
   * Extra class applied to the informational tooltip's portaled body. Required when this is
   * rendered inside a modal — the tooltip otherwise renders behind it (modals sit at the
   * topmost possible z-index; see RadioGroup's informationalTextTooltipBodyClassName).
   */
  tooltipBodyClassName?: string;
  /** Class applied to the div wrapping the radio group (and the callout, when rendered). */
  containerClassName?: string;
  /**
   * Renders SkipTimeDistanceRecalcCallout right below the radios when Skip is selected. Defaults
   * to true. Set to false when you want to render the callout separately somewhere else
   */
  showCallout?: boolean;
  /**
   * Lets a caller persist the "default already applied" flag outside this component's own instance.
   * Needed when this component unmounts/remounts within a session that should still only apply the
   * default once — e.g. the Volume Terminations wizard's Configure Report step. Omit both to fall back to
   * an internal ref, which is correct for the common case: a fresh mount (e.g. reopening a flyout)
   * is a fresh session and should always re-establish the default.
   */
  hasAppliedDefault?: boolean;
  onDefaultApplied?: () => void;
}

/**
 * The "Distance Measurement" control for opting a termination run out of recalculating average
 * and maximum time-and-distance.
 * Shared so it can be reused across every termination surface without re-deriving the option labels,
 * tooltip copy, or the enablement/defaulting logic.
 *
 * Owns its own applicability check and renders nothing when it doesn't apply
 * — callers don't need to compute or gate on any of that themselves, just pass the raw facts through.
 *
 * Reports its default back to the caller via setShouldSkip(true) on mount, when applicable —
 * feature flags load asynchronously (see .claude/docs/ui/feature-flag-caching.md), so this can't
 * just be a seeded initial prop; the caller's tracked value should start false/null and let this
 * component establish the real default. Fresh mount (e.g. reopening a flyout) always re-establishes
 * the default from scratch.
 */
export const DistanceMeasurementRadioGroup: FC<DistanceMeasurementRadioGroupProps> = ({ name, title, isZipProject, hasUnprocessedProviderTerminations = true, shouldSkip, setShouldSkip, computeTestId, skipTestId, tooltipBodyClassName, containerClassName, showCallout = true, hasAppliedDefault: externalHasAppliedDefault, onDefaultApplied }) => {
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const isEnabled = enabledFeatureFlags.has(Features.TERMINATION_SKIP_TIME_DISTANCE_RECALC) && !isZipProject && hasUnprocessedProviderTerminations;

  // Starts false so the effect below actually fires on a normal first mount, not
  // just the rare async-flag-reload case — and guards against firing again after that, so it can
  // never clobber a choice the user's since made.
  const internalHasAppliedDefault = useRef(false);
  useLayoutEffect(() => {
    const hasAppliedDefault = externalHasAppliedDefault ?? internalHasAppliedDefault.current;
    if (!hasAppliedDefault && isEnabled) {
      internalHasAppliedDefault.current = true;
      onDefaultApplied?.();
      setShouldSkip(true);
    }
  }, [isEnabled]);

  if (!isEnabled) {
    return null;
  }

  const handleChange = (event: ChangeEvent<HTMLElement, HTMLElement>) => {
    setShouldSkip((event.target as HTMLInputElement).value == true.toString());
  };

  return (
    <div className={containerClassName}>
      <QESRadioGroup
        groupLabel={title}
        informationalText="Controls whether this termination run recalculates average and maximum time and distance across the network. Select Skip for a considerably faster run — especially on larger terminations. Average/Max. values will not be included in the results. Select Calculate when you need the network-wide distance figures."
        informationalTextTooltipBodyClassName={tooltipBodyClassName}
        informationalTextTooltipWidth="800px"
      >
        <QESRadioButton
          label="Calculate average and maximum time and distance"
          name={name}
          value={false.toString()}
          checked={!shouldSkip}
          onChange={handleChange}
          testId={computeTestId}
        />
        <QESRadioButton
          label="Skip average and maximum time and distance calculation (faster)"
          name={name}
          value={true.toString()}
          checked={shouldSkip}
          onChange={handleChange}
          testId={skipTestId}
        />
      </QESRadioGroup>
      {showCallout && <SkipTimeDistanceRecalcCallout isSkipSelected={shouldSkip} testId={`${skipTestId}-callout`} />}
    </div>
  );
};
